import { useCallback } from "react";
import { readProcessItem, type ProcessItem } from "../../../api/content/processItemsApi";
import { isAbortError } from "../../../api/http/httpClient";
import { useLoad } from "../../../shared/loading/useLoad";
import { resolveVersionLabels } from "../../versions/labels/useVersionLabels";

/**
 * What an input card says about the item a step input holds (F61, F56): its kind, which is
 * its class in the DMW process ontology; its name; its readable version (ADR-0033); and, for
 * an artifact, what made the version. The graph reads the same facts with its catalogue;
 * a step reads them here for the items its plan prefills or a person picks.
 */
export interface ItemFacts {
  readonly kind: string;
  readonly name: string;
  readonly reference: string;
  readonly version: string | null;
  readonly origin: ItemOrigin | null;
}

/** What made an item's version: a person by hand, or a run of a Workflow. */
export type ItemOrigin = { readonly kind: "hand" } | { readonly kind: "workflow"; readonly name: string };

/** The facts of each reference that names an item; one that names none, or fails to read, is left out. */
export async function readItemFacts(projectId: string, references: readonly string[], signal: AbortSignal): Promise<ReadonlyMap<string, ItemFacts>> {
  const [items, labels] = await Promise.all([
    Promise.all(references.map(async (reference) => [reference, await orNothing(readProcessItem(reference, signal))] as const)),
    orNothing(resolveVersionLabels(projectId, { itemVersions: references, collectionVersions: [] }, signal)),
  ]);
  const facts = new Map<string, ItemFacts>();
  for (const [reference, item] of items) {
    if (item === null) continue;
    const { kind, name } = kindAndName(item);
    const origin = item.kind === "artifact" ? await artifactOrigin(item.artifact.madeBy, signal) : null;
    facts.set(reference, { kind, name, reference, version: labels?.get(reference)?.version ?? null, origin });
  }
  return facts;
}

const NO_FACTS: ReadonlyMap<string, ItemFacts> = new Map();

/** The facts of `references` in `projectId`; none without a project, while loading, or on failure. */
export function useItemFacts(projectId: string | undefined, references: readonly string[]): ReadonlyMap<string, ItemFacts> {
  // By value, so a new array with the same references doesn't load again.
  const key = [...new Set(references)].sort().join("\n");
  const load = useCallback(
    (signal: AbortSignal) => (projectId === undefined || key === "" ? Promise.resolve(NO_FACTS) : readItemFacts(projectId, key.split("\n"), signal)),
    [projectId, key],
  );
  const { state } = useLoad(load);
  return state.status === "loaded" ? state.value : NO_FACTS;
}

function kindAndName(item: ProcessItem): { readonly kind: string; readonly name: string } {
  switch (item.kind) {
    case "workflow":
      return { kind: "workflow", name: item.workflow.name };
    case "step":
      return { kind: "step", name: item.step.name };
    case "tool":
      return { kind: "tool", name: item.tool.name };
    case "artifact":
      return { kind: "artifact", name: item.artifact.name };
    case "other":
      return { kind: item.item.kind, name: item.item.name };
  }
}

/** By hand, or by the Workflow whose run made it, by name where it can be read. */
async function artifactOrigin(madeBy: { readonly workflow: string } | null, signal: AbortSignal): Promise<ItemOrigin> {
  if (madeBy === null) return { kind: "hand" };
  const workflow = await orNothing(readProcessItem(madeBy.workflow, signal));
  return { kind: "workflow", name: workflow?.kind === "workflow" ? workflow.workflow.name : madeBy.workflow };
}

/** A read the card can do without: it shows less, never an error. */
async function orNothing<T>(read: Promise<T>): Promise<T | null> {
  try {
    return await read;
  } catch (caught) {
    if (isAbortError(caught)) throw caught;
    return null;
  }
}
